// Import from NPM
// -------------------------------------
import React from "react";
import localforage from "localforage";
import { Router, Route, hashHistory } from "react-router";
import { syncHistoryWithStore } from "react-router-redux";
import loadable from "@loadable/component";
import "semantic-ui-css/semantic.min.css";
import "config/initializers/overrule.css";
import { Helmet } from "react-helmet";
import { Crisp } from "crisp-sdk-web";

// Import from Config
// -------------------------------------
import {
    Store,
    persistStore,
    persistStoreConfig,
} from "backend/storage/app.store";
import { getAppConfig } from "config/client.config";
import { appConfig } from "config/initializers/config.app";

// Import Helpers
// -------------------------------------
import { Loading } from "app/appComponents/Loading.react";

const LoadableAuthenticationContainer = loadable(
    () =>
        import(
            "app/authentication/AuthenticationContainer.react" /* webpackChunkName: "authentication" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableHelpConatainer = loadable(
    () =>
        import(
            "app/Help/HelpContainer.react.jsx" /* webpackChunkName: "helpDesk" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableAdminHomeContainer = loadable(
    () =>
        import(
            "app/adminPanels/AdminHomeContainer.react" /* webpackChunkName: "admin" */
        ),
    {
        fallback: <Loading />,
    }
);

const LoadableWelcome = loadable(
    () =>
        import(
            "app/home/WelcomeContainer.react" /* webpackChunkName: "welcomeEditor" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableCourseEditor = loadable(
    () =>
        import(
            "app/course/editor/CourseEditorContainer.react" /* webpackChunkName: "courseEditor" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableCreator = loadable(
    () =>
        import(
            "app/deck/editor/deckEditor/DeckExpressContainer.react" /* webpackChunkName: "deckEditor" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadablePaymentSuccess = loadable(
    () =>
        import(
            "app/appComponents/paymentPages/PaymentSuccess.react" /* webpackChunkName: "paymentSuccess" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadablePaymentFailure = loadable(
    () =>
        import(
            "app/appComponents/paymentPages/PaymentFailure.react" /* webpackChunkName: "paymentFailure" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableAnalyticsContainer = loadable(
    () =>
        import(
            "app/analytics/AnalyticsContainer.react" /* webpackChunkName: "analyticsContainer" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableSocialContainer = loadable(
    () =>
        import(
            "app/social/SocialContainer.react" /* webpackChunkName: "socialContainer" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableBloggingContainer = loadable(
    () =>
        import(
            "app/blog/BloggingContainer.react" /* webpackChunkName: "blogContainer" */
        ),
    {
        fallback: <Loading />,
    }
);
const ResetPassword = loadable(
    () => import("../app/authentication/ResetPasswordContainer.react"),
    {
        fallback: <Loading />,
    }
);
const AccountContainer = loadable(
    () => import("./appComponents/account/AccountContainer.react"),
    {
        fallback: <Loading />,
    }
);
const CrispChatBot = loadable(
    () => import("../app/Help/components/CrispChatBot.react.jsx"),
    {
        fallback: <Loading />,
    }
);
const Pricing = loadable(
    () => import("../app/appComponents/account/component/Pricing.react"),
    {
        fallback: <Loading />
    }
);
const AppSumoPlan = loadable(
    () => import("../app/appComponents/account/component/AppSumoPlan.react"),
    {
        fallback: <Loading />
    }
);

class App extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            rehydrated: false,
        };
        this.requireLogin = this.requireLogin.bind(this);
        this.alreadyLoggedIn = this.alreadyLoggedIn.bind(this);
        this.manageStore = window.manageStore = this.manageStore.bind(this);
        this.appType = window.appType = "drive";
        this.manageStore();
    }

    manageStore = (purge) => {
        if (purge === "purge") {
            persistStore(Store, persistStoreConfig).purge();
            localforage.removeItem("tokenHash");
        } else {
            if (window.localStorage) {
                persistStore(Store, persistStoreConfig, () => {
                    let existing_user = window.localStorage.getItem(
                        "reduxPersist:auth"
                    );
                    if (existing_user) {
                        localforage.setItem("reduxPersist:auth", existing_user);
                    }
                    this.setState({ rehydrated: true });
                });
            }
        }
    };

    requireLogin(nextState, replace, callback) {
        if (
            nextState.location.pathname !== "/drive/login" &&
            process.env.NODE_ENV !== "development"
        ) {
            Crisp.configure(getAppConfig().crispWebsiteId, {
                autoload: true,
            });
            Crisp.chat.show();
        }
        if (!Store.getState().auth.isUserLoggedIn) {
            replace("/drive/login");
        }
        callback();
    }

    alreadyLoggedIn(nextState, replace, callback) {
        if (
            ["/drive/login", "/login"].includes(nextState.location.pathname) &&
            process.env.NODE_ENV !== "development"
        ) {
            Crisp.configure(getAppConfig().crispWebsiteId, {
                autoload: false,
            });
            if (Store.getState().auth.isUserLoggedIn) {
                Crisp.session.reset();
            }
            Crisp.chat.hide();
        }
        if (Store.getState().auth.isUserLoggedIn) {
            if (
                ["Inductor", "FreeInductor"].includes(
                    Store.getState().auth.user.license.license_type
                )
            ) {
                replace("/drive?panel=welcome");
            } else {
                replace(appConfig.auth.routes.afterSignInDashboard);
            }
        }
        callback();
    }

    logout = () => {
        hashHistory.push("/logout");
        window.location.reload();
    };

    render() {
        let noOverflow = {
            height: "100vh",
        };
        let appStyle = {
            ...getAppConfig().appStyle,
            position: "relative",
            height: "100%",
            background: "inherit",
            overflow: "auto",
        };
        let gtmId = getAppConfig().gTagId ? getAppConfig().gTagId : "";
        let returnValue = (
            <div style={noOverflow}>
                <Helmet>
                    <meta charset="utf-8" />
                    <script async src={`https://www.googletagmanager.com/gtag/js?id=${gtmId}`}></script>
                    <script>
                        {`
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());

                        gtag('config', '${gtmId}', {
                            'send_page_view' : false
                        });
                        `}
                    </script>

                    <script>
                        {`!function (f, b, e, v, n, t, s) {
                            if (f.fbq) return; n = f.fbq = function () {
                                n.callMethod ?
                                n.callMethod.apply(n, arguments) : n.queue.push(arguments)
                            };
                            if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
                            n.queue = []; t = b.createElement(e); t.async = !0;
                            t.src = v; s = b.getElementsByTagName(e)[0];
                            s.parentNode.insertBefore(t, s)
                        }(window, document, 'script',
                            'https://connect.facebook.net/en_US/fbevents.js');
                        fbq('init', '663537658904907');
                        fbq('track', 'PageView');`
                        }
                    </script>
                    <noscript>
                        {`<img height="1" width="1" style="display:none"
                        src="https://www.facebook.com/tr?id=663537658904907&ev=PageView&noscript=1" />
                        `}
                    </noscript>
                    <meta
                        name="viewport"
                        content="user-scalable=0,
                   initial-scale=1.0,
                   maximum-scale=3.0,
                   minimum-scale=1.0,
                   width=device-width"
                    />
                    <meta
                        http-equiv="Content-Security-Policy"
                        content="default-src * 'unsafe-inline' 'self' data: gap: https://ssl.gstatic.com 'unsafe-eval';img-src * 'self' data: blob: https: android-webview-video-poster:; style-src 'self' 'unsafe-inline' * https:; media-src *  'self' data: blob: https:;"
                    />
                    <meta name="author" content="QuoDeck Technologies PLC" />
                    <meta
                        name="description"
                        content="Start training on your game-based mobile learning platform. QuoDeck helps businesses, startups, teachers and professors to train their audience in a fast, immersive and easy manner."
                    />
                    <meta name="theme-color" content="#000000" />

                    <link rel="manifest" href="manifest.json" />
                    <link rel="shortcut icon" href="favicon.ico" />
                    <title>
                        {appConfig.appName
                            ? appConfig.appName.dashboard
                            : "QuoDeck: The Game-based Mobile Learning App Builder"}
                    </title>
                </Helmet>
                <div style={appStyle}>
                    <Router history={syncHistoryWithStore(hashHistory, Store)}>
                        <Route component={AuthenticatedLayout}>
                            <Route
                                path="/payment_success"
                                component={LoadablePaymentSuccess}
                            />
                            <Route
                                path="/payment_failure"
                                component={LoadablePaymentFailure}
                            />
                            <Route
                                path="/drive"
                                component={LoadableCourseEditor}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/analytics"
                                component={LoadableAnalyticsContainer}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/social"
                                component={LoadableSocialContainer}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/blogging"
                                component={LoadableBloggingContainer}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/deckEditor/:topicId(/:readerType)(/:deckId)"
                                component={LoadableCreator}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/courseEditor"
                                component={LoadableCourseEditor}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/"
                                component={LoadableWelcome}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/admin"
                                component={LoadableAdminHomeContainer}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/resetPassword"
                                component={ResetPassword}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/upgrade"
                                component={AccountContainer}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/pricing"
                                component={Pricing}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/plan"
                                component={AppSumoPlan}
                                onEnter={this.requireLogin}
                            />
                        </Route>
                        <Route component={UnauthenticatedLayout}>
                            <Route
                                path="/:appName/login"
                                component={LoadableAuthenticationContainer}
                                onEnter={this.alreadyLoggedIn}
                            />
                            <Route
                                path="/login"
                                component={LoadableAuthenticationContainer}
                                onEnter={this.alreadyLoggedIn}
                            />
                            <Route
                                path="/register"
                                component={LoadableAuthenticationContainer}
                                onEnter={this.alreadyLoggedIn}
                            />
                            <Route
                                path="/appsumoregister"
                                component={LoadableAuthenticationContainer}
                                onEnter={this.alreadyLoggedIn}
                            />
                            <Route
                                path="/logout"
                                component={LoadableAuthenticationContainer}
                                onEnter={this.requireLogin}
                            />
                            <Route
                                path="/help"
                                component={LoadableHelpConatainer}
                            // onEnter={this.alreadyLoggedIn}
                            />
                            <Route
                                path="/forgotPassword"
                                component={LoadableAuthenticationContainer}
                                onEnter={this.alreadyLoggedIn}
                            />
                        </Route>
                    </Router>
                </div>
            </div>
        );
        /*   if (isPortrait) {
            return (
                <ErrorPage
                    msg={
                        "The Inductor side of the platform is currently not available for mobile devices."
                    }
                    logout={true}
                    errorImage="https://media.istockphoto.com/id/1139913278/vector/woman-with-laptop-sitting-in-nature-and-leaves-concept-illustration-for-working-freelancing.jpg?s=612x612&w=0&k=20&c=Uox6lJYdis3DmG3qdP8x06q4WZdVKSUR8LyL6O3-TWo="
                    buttons=" "
                    defaultMsg=" "
                    header=" "
                />
            )
        }
        else  */ if (
            !this.state.rehydrated
        ) {
            returnValue = <Loading />;
        }
        return returnValue;
    }
}

const AuthenticatedLayout = (props) => {
    return (
        <div>
            {props.children}
            {process.env.NODE_ENV !== "development" && <CrispChatBot />}
        </div>
    );
};

const UnauthenticatedLayout = (props) => <div>{props.children}</div>;

export default App;
